import React, { useState, useRef, useEffect } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, X } from 'lucide-react';
import { ASSET_CLASS_ICONS } from '@/constants/investmentIcons';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getCurrencyLabel } from '@/lib/currency';

interface InlineAddAssetRowProps {
  onAssetAdded: () => void;
}

export function InlineAddAssetRow({ onAssetAdded }: InlineAddAssetRowProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [assetType, setAssetType] = useState<string>('');
  const [assetName, setAssetName] = useState('');
  const [marketValue, setMarketValue] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState<Array<{ code: string; symbol?: string }>>([]);
  
  const rowRef = useRef<HTMLTableRowElement>(null);
  const selectTriggerRef = useRef<HTMLButtonElement>(null);

  // Helper to find scrollable ancestor
  const getScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;
    const { overflow, overflowY } = window.getComputedStyle(element);
    if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
      return element;
    }
    return getScrollableAncestor(element.parentElement);
  };

  // Handle scroll and focus when form opens
  useEffect(() => {
    let raf1: number | null = null;
    let raf2: number | null = null;
    if (isAdding && rowRef.current) {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          const scrollContainer = getScrollableAncestor(rowRef.current);
          if (scrollContainer && rowRef.current) {
            // Scroll container to bottom to show the new row
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
          // Focus the select trigger
          selectTriggerRef.current?.focus();
        });
      });
    }
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isAdding]);

  // Load currencies from table for consistency across app
  useEffect(() => {
    let cancelled = false;
    async function loadCurrencies() {
      try {
        const { data: cur, error } = await supabase
          .from('currencies')
          .select('code, symbol')
          .eq('is_active', true)
          .order('code');
        if (!cancelled) {
          if (error) throw error;
          if (Array.isArray(cur) && cur.length > 0) {
            setCurrencyOptions(cur as any);
            // Keep existing selected currency; if empty default to CAD or first active
            if (!currency) setCurrency('CAD');
          }
        }
      } catch {
        if (!cancelled) {
          setCurrencyOptions([
            { code: 'CAD', symbol: '$' },
            { code: 'USD', symbol: '$' },
            { code: 'EUR', symbol: '€' },
            { code: 'GBP', symbol: '£' },
            { code: 'AUD', symbol: '$' },
          ]);
        }
      }
    }
    if (isAdding) loadCurrencies();
    return () => { cancelled = true; };
  }, [isAdding]);

  const assetTypes = [
    { value: 'equity', label: 'Equity', icon: ASSET_CLASS_ICONS.equity },
    { value: 'fixed_income', label: 'Fixed Income', icon: ASSET_CLASS_ICONS.fixed_income },
    { value: 'alternative', label: 'Alternative', icon: ASSET_CLASS_ICONS.alternative },
    { value: 'cash', label: 'Cash', icon: ASSET_CLASS_ICONS.cash },
    { value: 'multi_asset', label: 'Multi-Asset', icon: ASSET_CLASS_ICONS.multi_asset },
  ];

  const handleSubmit = async () => {
    if (!assetType || !assetName.trim() || !marketValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    const value = parseFloat(marketValue);
    if (isNaN(value) || value <= 0) {
      toast.error('Please enter a valid value');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to add assets');
        return;
      }

      const { error } = await supabase.from('investment_assets').insert({
        user_id: user.id,
        asset_name: assetName.trim(),
        asset_type: assetType,
        market_value: value,
        currency: currency,
        is_manual: true,
        pricing_method: 'user_input',
        as_of: new Date().toISOString(),
        quantity: 1, // Default quantity
      });

      if (error) throw error;

      toast.success('Asset added successfully');
      
      // Reset form
      setAssetType('');
      setAssetName('');
      setMarketValue('');
      setCurrency('CAD');
      setIsAdding(false);
      
      // Notify parent to refresh
      onAssetAdded();
    } catch (error: any) {
      console.error('Error adding asset:', error);
      toast.error(error.message || 'Failed to add asset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setAssetType('');
    setAssetName('');
    setMarketValue('');
    setCurrency('CAD');
    setIsAdding(false);
  };

  if (!isAdding) {
    return (
      <TableRow className="border-b-0 hover:bg-muted/40">
        <TableCell colSpan={7} className="p-0 overflow-hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(true)}
            className="w-full py-3 bg-foreground/60 text-background hover:bg-foreground/90 hover:text-background rounded-tl-none rounded-tr-none rounded-bl-md rounded-br-md font-medium transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Quick Add Asset
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  const SelectedIcon = assetType ? assetTypes.find(t => t.value === assetType)?.icon : null;

  return (
    <TableRow ref={rowRef} className="border-b-0 bg-muted/60">
      <TableCell>{/* Checkbox column - empty */}</TableCell>
      <TableCell colSpan={5}>{/* Spans: Asset + Source + Account + Currency + Value columns */}
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-2">
          <Select value={assetType} onValueChange={setAssetType}>
            <SelectTrigger ref={selectTriggerRef} className="h-8 w-36 sm:w-44 -ml-2 sm:-ml-3">
              <SelectValue className="truncate" placeholder="Class">
                {SelectedIcon && (
                  <div className="flex items-center gap-2">
                    <SelectedIcon className="h-4 w-4" />
                    <span className="text-xs hidden sm:inline truncate">{assetTypes.find(t => t.value === assetType)?.label}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {assetTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          
          <Input
            placeholder="Asset name"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            className="h-8 flex-1 min-w-[120px]"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
              if (e.key === 'Escape') handleCancel();
            }}
          />
          
          <Input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={marketValue}
            onChange={(e) => setMarketValue(e.target.value)}
            className="h-8 w-20 sm:w-24 text-right"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
              if (e.key === 'Escape') handleCancel();
            }}
          />
          
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="h-8 w-20 sm:w-24 truncate">
              <SelectValue className="truncate" />
            </SelectTrigger>
            <SelectContent>
              {(currencyOptions.length > 0 ? currencyOptions : [
                { code: 'CAD', symbol: '$' },
                { code: 'USD', symbol: '$' },
                { code: 'EUR', symbol: '€' },
                { code: 'GBP', symbol: '£' },
                { code: 'AUD', symbol: '$' },
              ]).map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {getCurrencyLabel(c.code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </TableCell>
      <TableCell>{/* Actions column */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
